import React, { useCallback, useEffect, useRef, useState } from "react";
import { getPublishStatus, publishMinisite } from "../../api/minisite";

const POLL_INTERVAL_MS = 3000;

export function computeState(draft) {
  const missing = [];
  if (!draft.slug) missing.push("slug");
  if (!draft.hero?.photo_filename) missing.push("photo hero");
  if ((draft.chapters || []).length < 1) missing.push("au moins 1 chapitre");
  if (missing.length) return { state: "invalid", missing };
  if (draft.minisite_status === "outdated") return { state: "outdated" };
  if (draft.minisite_status === "published") return { state: "published" };
  return { state: "not_published" };
}

function formatPublicationDate(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat("fr-FR", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

export default function PublishButton({ clientId, draft, onPublished }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const timer = useRef(null);
  const mounted = useRef(true);
  const { state, missing = [] } = computeState(draft);
  const publicationDate = formatPublicationDate(draft.last_published_at);

  const stopPolling = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  // La publication tourne côté serveur : on ne peut pas l'annuler en fermant
  // l'écran, mais on doit arrêter d'écrire dans un composant démonté.
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  const applyJob = useCallback(
    (job) => {
      if (!mounted.current) return;
      // Deux appelants peuvent arriver ici : la reprise de suivi au montage et
      // un clic. Sans ce nettoyage, le second orpheline le timer du premier et
      // deux chaînes de polling tournent en parallèle.
      stopPolling();
      if (job.status === "running") {
        timer.current = setTimeout(async () => {
          try {
            applyJob(await getPublishStatus(clientId));
          } catch (e) {
            if (!mounted.current) return;
            setError(e?.response?.data?.detail || e.message);
            setBusy(false);
          }
        }, POLL_INTERVAL_MS);
        return;
      }
      stopPolling();
      setBusy(false);
      if (job.status === "error") {
        setError(job.error || "La publication a échoué.");
        return;
      }
      if (job.status === "done") {
        setResult(job);
        setNotice(null);
        if (onPublished) onPublished();
      }
    },
    [clientId, onPublished, stopPolling]
  );

  // Une publication lancée avant un rechargement de page continue côté
  // serveur : on reprend son suivi à l'ouverture plutôt que d'afficher un
  // bouton inerte.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const job = await getPublishStatus(clientId);
        if (cancelled || !mounted.current) return;
        if (job.status === "running") {
          setBusy(true);
          setNotice("Une publication est déjà en cours.");
          applyJob(job);
        }
      } catch (e) {
        /* pas de job connu : rien à reprendre */
      }
    })();
    return () => {
      cancelled = true;
      // Le timer éventuellement armé par cette reprise doit mourir avec elle.
      // Il se trouve qu'un démontage le purge déjà par un autre chemin, mais
      // compter là-dessus revient à dépendre d'un accident.
      stopPolling();
    };
  }, [clientId, applyJob, stopPolling]);

  let label;
  let color;
  let title;
  let disabled = busy;
  if (state === "invalid") {
    label = `Incomplet — manque: ${missing.join(", ")}`;
    color = "#999";
    disabled = true;
  } else if (state === "not_published") {
    label = "Publier";
    color = "#10b981";
  } else if (state === "outdated") {
    label = "Republier";
    color = "#f59e0b";
  } else {
    // Volontairement PAS `disabled`. Avant que l'état soit calculé côté
    // serveur, « publié » était inatteignable (les deux dates étaient
    // comparées brutes, donc tout site publié ressortait « modifié ») et
    // cette branche était morte. La rendre atteignable sans la déverrouiller
    // couperait le seul point d'entrée de publication de l'écran : le
    // contenu en ligne peut changer sans que `updated_at` bouge — une planche
    // BD revalidée, un gabarit livré par un déploiement — et une publication
    // interrompue après `deploy_files` laisse le bloc `cloudflare` posé.
    label = "Publié ✓";
    color = "#666";
    title = "Le site en ligne est à jour. Cliquer republie quand même — utile après une planche BD revalidée ou une publication interrompue.";
  }

  const handlePublish = async () => {
    // Le bouton « Publié ✓ » reste cliquable exprès (voir plus haut), mais un clic
    // par mégarde relance un déploiement Cloudflare complet sur un site déjà à jour.
    if (state === "published" && !window.confirm(
      "Ce site est déjà à jour en ligne. Le republier quand même ?"
    )) {
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    setResult(null);
    try {
      const job = await publishMinisite(clientId);
      if (job.already_running) {
        setNotice("Une publication est déjà en cours — inutile de relancer.");
      }
      applyJob(job);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
      setBusy(false);
    }
  };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: 12,
                  background: "#fafafa", borderRadius: 8, flexWrap: "wrap" }}>
      <button
        disabled={disabled}
        onClick={handlePublish}
        title={title}
        style={{
          background: color,
          color: "white",
          border: "none",
          padding: "10px 20px",
          borderRadius: 6,
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.6 : 1,
          fontWeight: 600,
        }}
      >
        {busy ? "Publication en cours..." : label}
      </button>
      {!busy && state === "published" && publicationDate && (
        <span style={{ fontSize: 12, color: "#666" }}>
          Publié le {publicationDate}
        </span>
      )}
      {!busy && state === "outdated" && publicationDate && (
        <span style={{ fontSize: 12, color: "#b45309", fontWeight: 600 }}>
          Modifié depuis la publication du {publicationDate}
        </span>
      )}
      {busy && (
        <span style={{ fontSize: 12, color: "#666" }}>
          Cela prend souvent plus de deux minutes. Vous pouvez quitter cet
          écran, la publication continue.
        </span>
      )}
      {notice && <span style={{ fontSize: 12, color: "#b45309" }}>{notice}</span>}
      {error && <span style={{ color: "red", fontSize: 12 }}>{error}</span>}
      {result && (
        <span style={{ fontSize: 12 }}>
          v{result.version_number} →{" "}
          <a href={result.live_url} target="_blank" rel="noreferrer">
            {result.live_url}
          </a>
        </span>
      )}
    </div>
  );
}
